import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography } from '@mui/material';
import { resolveSlotValues, getDTWidgetSlots } from '../../registry/dtWidgetTypes';
import { useDTDemoMap } from '../../hooks/useDTDemoTick';
import { hasLiveSlotValue } from '../../utils/dtDemoFallback';
import { useContainerSize } from '../../hooks/useContainerSize';

const DEFAULT_SLOTS = getDTWidgetSlots('dt-metering-bars');
const CURRENT_SLOT_KEYS = ['ir', 'iy', 'ib', 'in'];
const VOLTAGE_SLOT_KEYS = ['vry', 'vyb', 'vbr'];

const PHASE_COLORS = {
  ir: '#DC272D',
  iy: '#FFD000',
  ib: '#008CD7',
  in: '#C1C1C1',
  vry: '#DC272D',
  vyb: '#FFD000',
  vbr: '#008CD7',
};

function makeBarOptions(labels, colors, theme) {
  return {
    chart: { type: 'bar', toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true } },
    xaxis: { categories: labels, labels: { show: false } },
    yaxis: {
      labels: {
        style: {
          colors: theme === 'dark' ? ['#fff', '#fff', '#fff', '#fff'] : ['#000', '#000', '#000', '#000'],
          fontSize: '14px',
          fontWeight: 'bold',
        },
      },
    },
    colors,
    tooltip: { enabled: false },
    states: { hover: { filter: { type: 'none' } } },
  };
}

function DTMeteringBars({ widget, data, theme }) {
  const tagprops = widget?.props?.extended?.tagprops || [];
  const liveData = Array.isArray(data) ? data : [];
  const slotValues = resolveSlotValues(tagprops, liveData);
  const demoMap = useDTDemoMap('dt-metering-bars');
  const { ref: rootRef, width: rw } = useContainerSize(220, 200);
  const { ref: currRef, height: currBoxH } = useContainerSize(80, 60);
  const { ref: voltRef, height: voltBoxH } = useContainerSize(80, 56);

  const currentSlots = DEFAULT_SLOTS.filter((s) => CURRENT_SLOT_KEYS.includes(s.key));
  const voltageSlots = DEFAULT_SLOTS.filter((s) => VOLTAGE_SLOT_KEYS.includes(s.key));

  const pickNum = (slot) => {
    const resolved = slotValues[slot.key];
    if (hasLiveSlotValue(resolved)) return parseFloat(resolved.value) || 0;
    return Number(demoMap[slot.key]) || 0;
  };

  const currentLabels = currentSlots.map((s) => slotValues[s.key]?.alias || s.label);
  const currentValues = currentSlots.map(pickNum);
  const currentColors = currentSlots.map((s) => PHASE_COLORS[s.key] || '#C1C1C1');

  const voltageLabels = voltageSlots.map((s) => slotValues[s.key]?.alias || s.label);
  const voltageValues = voltageSlots.map(pickNum);
  const voltageColors = voltageSlots.map((s) => PHASE_COLORS[s.key] || '#C1C1C1');

  const capH = Math.max(40, Math.floor(currBoxH));
  const voltH = Math.max(40, Math.floor(voltBoxH));

  return (
    <Box
      ref={rootRef}
      sx={{
        flex: 1,
        minHeight: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 1,
        boxSizing: 'border-box',
      }}
    >
      <Typography variant="caption" sx={{ color: theme === 'dark' ? '#aaa' : '#555', fontWeight: 700, flexShrink: 0 }}>
        Current (A)
      </Typography>
      <Box ref={currRef} sx={{ flex: '1.15 1 0', minHeight: 48, width: '100%', minWidth: 0 }}>
        {rw > 0 && capH > 0 && (
          <ReactApexChart
            options={makeBarOptions(currentLabels, currentColors, theme)}
            series={[{ name: 'Current', data: currentValues }]}
            type="bar"
            height={capH}
            width={Math.max(60, rw - 16)}
          />
        )}
      </Box>
      <Typography variant="caption" sx={{ color: theme === 'dark' ? '#aaa' : '#555', fontWeight: 700, flexShrink: 0, mt: 0.5 }}>
        Voltage (V)
      </Typography>
      <Box ref={voltRef} sx={{ flex: 1, minHeight: 44, width: '100%', minWidth: 0 }}>
        {rw > 0 && voltH > 0 && (
          <ReactApexChart
            options={makeBarOptions(voltageLabels, voltageColors, theme)}
            series={[{ name: 'Voltage', data: voltageValues }]}
            type="bar"
            height={voltH}
            width={Math.max(60, rw - 16)}
          />
        )}
      </Box>
    </Box>
  );
}

export default React.memo(DTMeteringBars);
